import type { StoreSchema } from '@rstore/shared';
import type { FetchFunction } from '#layers/base/public';
import { WorkspaceEntitlementResource } from './access/entitlements';
import { Policy, RawPolicy, RolePolicy } from './access/policies';
import {
	createRolePolicyRelations,
	createRoleRelations,
	createUserRoleRelations,
	createWorkspaceMemberRelations,
	RawPolicyRelations,
} from './access/relations';
import { createWorkspaceRole, UserRole } from './access/roles';
import { createWorkspaceMember } from './access/workspace-member';
import { Block } from './block';
import { WorkspaceDataConnector, WorkspaceExtension } from './extension';
import { createWorkspaceFile } from './file';
import { createWorkspaceInfo } from './info';
import { KeyStorage } from './key-storage';
import {
	createFileRelations,
	KeyStorageRelations,
	PageRelations,
	PageSectionRelations,
	SpaceRelations,
} from './relations';
import { Collection, CollectionMeta } from './schema/collection';
import { SingleConstraint, SingleConstraintField } from './schema/constraint';
import { DataSource } from './schema/data-source';
import { Index, IndexField } from './schema/indexes';
import { Namespace } from './schema/namespace';
import { PrimitiveField, PrimitiveFieldMeta } from './schema/primitive-field';
import { SingleRelationField, SingleRelationFieldMeta } from './schema/relation-field';
import {
	CollectionMetaRelations,
	CollectionRelations,
	DataSourceRelations,
	IndexFieldRelations,
	IndexRelations,
	NamespaceRelations,
	PrimitiveFieldMetaRelations,
	PrimitiveFieldRelations,
	SingleConstraintFieldRelations,
	SingleConstraintRelations,
	SingleRelationFieldMetaRelations,
	SingleRelationFieldRelations,
} from './schema/relations';
import { Settings } from './settings';
import { Page, PageSection, Space } from './spaces';

export function createWorkspaceSchema(workspaceFetch: FetchFunction) {
	const role = createWorkspaceRole(workspaceFetch);
	const workspaceMember = createWorkspaceMember(workspaceFetch);
	const file = createWorkspaceFile(workspaceFetch);
	const info = createWorkspaceInfo(workspaceFetch);

	return [
		WorkspaceEntitlementResource,
		Policy,
		RawPolicy,
		RolePolicy,
		role,
		UserRole,
		workspaceMember,
		Block,
		WorkspaceExtension,
		WorkspaceDataConnector,
		file,
		info,
		KeyStorage,
		Settings,
		Space,
		Page,
		PageSection,
		Collection,
		CollectionMeta,
		SingleConstraint,
		SingleConstraintField,
		DataSource,
		Index,
		IndexField,
		Namespace,
		PrimitiveField,
		PrimitiveFieldMeta,
		SingleRelationField,
		SingleRelationFieldMeta,
		RawPolicyRelations,
		createRolePolicyRelations(role),
		createRoleRelations(role),
		createUserRoleRelations(role),
		createWorkspaceMemberRelations(workspaceMember),
		createFileRelations(file),
		KeyStorageRelations,
		PageRelations,
		PageSectionRelations,
		SpaceRelations,
		CollectionMetaRelations,
		CollectionRelations,
		DataSourceRelations,
		IndexFieldRelations,
		IndexRelations,
		NamespaceRelations,
		PrimitiveFieldMetaRelations,
		PrimitiveFieldRelations,
		SingleConstraintFieldRelations,
		SingleConstraintRelations,
		SingleRelationFieldMetaRelations,
		SingleRelationFieldRelations,
	] satisfies StoreSchema;
}
